import { Injectable } from '@angular/core';
import API, { graphqlOperation } from '@aws-amplify/api-graphql';

/**
 * Posting rule for bank statement rows. Rules are stored per company bank
 * account (the RACUN of the statement being processed): a rule created while
 * working on account A is never applied to a statement of account B.
 *
 * The non-empty opis1 / opis2 / opis3 / svrha fields form the condition. A
 * row matches when every non-empty condition holds: opis1 (the partner
 * account) must be equal, the text fields only have to be contained in the
 * row's value, so a rule on "PRENOS SREDSTAVA" also matches
 * "NALOG ZA PRILIV ... - PRENOS SREDSTAVA RFK23072026".
 */
export interface BankPostingRule {
  id?: string;
  clientID: string;
  companyAccount: string;
  clientIDCompanyAccountPK?: string;
  opis1?: string | null;
  opis2?: string | null;
  opis3?: string | null;
  svrha?: string | null;
  konto?: string | null;
  partnerCode?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** The row values a rule is matched against. */
export interface BankStatementRowValues {
  opis1: string;
  opis2: string;
  opis3: string;
  svrha: string;
}

const RULE_FIELDS = `
  id
  clientID
  companyAccount
  clientIDCompanyAccountPK
  opis1
  opis2
  opis3
  svrha
  konto
  partnerCode
  createdAt
  updatedAt
`;

const CONDITION_KEYS: (keyof BankStatementRowValues)[] = ['opis1', 'opis2', 'opis3', 'svrha'];

@Injectable({ providedIn: 'root' })
export class BankPostingRuleService {
  /** Cache per company account so reopening the preview does not refetch. */
  private cache: { [pk: string]: BankPostingRule[] } = {};

  static normalizeAccount(account: string): string {
    return (account || '').replace(/\D/g, '');
  }

  static pk(clientID: string, companyAccount: string): string {
    return `${clientID}#${BankPostingRuleService.normalizeAccount(companyAccount)}`;
  }

  private static norm(value: string | null | undefined): string {
    return (value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  /** True when the rule has at least one condition field set. */
  static hasCondition(rule: Partial<BankPostingRule>): boolean {
    return CONDITION_KEYS.some((k) => !!(rule[k] || '').toString().trim());
  }

  static matches(rule: BankPostingRule, row: BankStatementRowValues): boolean {
    if (!BankPostingRuleService.hasCondition(rule)) return false;
    for (const key of CONDITION_KEYS) {
      const cond = BankPostingRuleService.norm(rule[key]);
      if (!cond) continue;
      const value = BankPostingRuleService.norm(row[key]);
      if (key === 'opis1' ? cond !== value : !value.includes(cond)) return false;
    }
    return true;
  }

  /**
   * The rule to apply to a row: among all matching rules the most recently
   * created one wins.
   */
  static findMatch(rules: BankPostingRule[], row: BankStatementRowValues): BankPostingRule | null {
    const matching = rules.filter((r) => BankPostingRuleService.matches(r, row));
    if (!matching.length) return null;
    matching.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    return matching[0];
  }

  // ─── CRUD ─────────────────────────────────────────────────────────────────

  async listForAccount(clientID: string, companyAccount: string, force = false): Promise<BankPostingRule[]> {
    const pk = BankPostingRuleService.pk(clientID, companyAccount);
    if (!force && this.cache[pk]) return this.cache[pk];

    const statement = `query ListBankStatementPostingRulesByCompanyAccount($pk: String!, $limit: Int, $nextToken: String) {
      listBankStatementPostingRulesByCompanyAccount(clientIDCompanyAccountPK: $pk, sortDirection: DESC, limit: $limit, nextToken: $nextToken) {
        items { ${RULE_FIELDS} }
        nextToken
      }
    }`;

    let items: BankPostingRule[] = [];
    let nextToken: string | null = null;
    do {
      const args: any = { pk, limit: 1000 };
      if (nextToken) args.nextToken = nextToken;
      const response = (await API.graphql(graphqlOperation(statement, args))) as any;
      const page = response.data.listBankStatementPostingRulesByCompanyAccount;
      items = items.concat(page?.items || []);
      nextToken = page?.nextToken || null;
    } while (nextToken);

    this.cache[pk] = items;
    return items;
  }

  async create(rule: BankPostingRule): Promise<BankPostingRule> {
    const statement = `mutation CreateBankStatementPostingRule($input: CreateBankStatementPostingRuleInput!) {
      createBankStatementPostingRule(input: $input) { ${RULE_FIELDS} }
    }`;
    const input = this.toInput(rule);
    const response = (await API.graphql(graphqlOperation(statement, { input }))) as any;
    const created = response.data.createBankStatementPostingRule as BankPostingRule;
    this.invalidate(created.clientID, created.companyAccount);
    return created;
  }

  async update(rule: BankPostingRule): Promise<BankPostingRule> {
    if (!rule.id) throw new Error('Rule id is required for update');
    const statement = `mutation UpdateBankStatementPostingRule($input: UpdateBankStatementPostingRuleInput!) {
      updateBankStatementPostingRule(input: $input) { ${RULE_FIELDS} }
    }`;
    const input = { id: rule.id, ...this.toInput(rule) };
    const response = (await API.graphql(graphqlOperation(statement, { input }))) as any;
    const updated = response.data.updateBankStatementPostingRule as BankPostingRule;
    this.invalidate(updated.clientID, updated.companyAccount);
    return updated;
  }

  async delete(rule: BankPostingRule): Promise<void> {
    if (!rule.id) return;
    const statement = `mutation DeleteBankStatementPostingRule($input: DeleteBankStatementPostingRuleInput!) {
      deleteBankStatementPostingRule(input: $input) { id }
    }`;
    await API.graphql(graphqlOperation(statement, { input: { id: rule.id } }));
    this.invalidate(rule.clientID, rule.companyAccount);
  }

  invalidate(clientID: string, companyAccount: string): void {
    delete this.cache[BankPostingRuleService.pk(clientID, companyAccount)];
  }

  private toInput(rule: BankPostingRule) {
    const companyAccount = BankPostingRuleService.normalizeAccount(rule.companyAccount);
    const clean = (v: string | null | undefined) => {
      const t = (v || '').trim();
      return t ? t : null;
    };
    return {
      clientID: rule.clientID,
      companyAccount,
      clientIDCompanyAccountPK: BankPostingRuleService.pk(rule.clientID, companyAccount),
      opis1: clean(rule.opis1),
      opis2: clean(rule.opis2),
      opis3: clean(rule.opis3),
      svrha: clean(rule.svrha),
      konto: clean(rule.konto),
      partnerCode: clean(rule.partnerCode),
    };
  }
}
